import { h, Fragment } from 'preact';
import { useCallback, useState, useEffect, useRef } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Zoom, Thumbs } from 'swiper';
import Image from 'utils/image';
import Video from 'utils/reactVideo';
import useWindowSize from 'utils/use-window-size';
import ImageZoom from 'react-image-zooom';
import { StyleTransition } from 'preact-transitioning';
import { useProduct } from './product-context';
import Icon from 'utils/icon';
import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/thumbs';
import './product-gallery.css';
import ProductModels from './product-models';

const ProductGalleryMedia = ({
  media,
  handleImageLoad,
  handleVideoLoad,
  isThumbnail = false,
}) => {
  const mediaType = media.mediaContentType.toLowerCase();
  const [vimeoPlayer, setVimeoPlayer] = useState(null);
  const { isMobile } = useWindowSize();
  const onPlayerInit = useCallback((player) => setVimeoPlayer(player), []);

  if (mediaType === 'image') {
    // Simplified rendering for thumbnails
    if (isThumbnail) {
      return (
        <div className="aspect-[11/14] relative cursor-pointer">
          <Image
            src={media.previewImage.transformedSrc}
            alt={media.image.altText}
            isTransformed={true}
            srcWidths={{ sm: 40, md: 80, lg: 120, xl: 150, '2xl': 150 }}
            additionalClasses="relative z-1 object-cover h-full w-full"
          />
          <div class="absolute top-0 w-full h-full aspect-[11/14] bg-gray-100"></div>
        </div>
      );
    }

    return (
      <div
        className={`aspect-[11/14] relative ${
          isMobile ? 'swiper-zoom-container' : ''
        }`}
      >
        {isMobile ? (
          <Image
            src={media.previewImage.transformedSrc}
            alt={media.image.altText}
            isTransformed={true}
            srcWidths={{ sm: 40, md: 168, lg: 324, xl: 480, '2xl': 536 }}
            additionalClasses="relative swiper-zoom-target z-1 object-cover h-full w-full"
          />
        ) : (
          <StyleTransition
            in={true}
            duration={300}
            styles={{
              enter: { opacity: 0 },
              enterActive: { opacity: 1 },
            }}
          >
            <ImageZoom
              src={media.previewImage.transformedSrc}
              alt={media.image.altText}
              style={{ transition: 'opacity 300ms' }}
              className="!absolute z-1 object-cover h-full !w-full [&>img]:w-full [&>img]:h-full [&>img]:object-cover"
              width="100%"
              height="auto"
            />
          </StyleTransition>
        )}
        <div class="absolute top-0 w-full h-full aspect-[11/14] bg-gray-100"></div>
      </div>
    );
  }

  if (mediaType === 'external_video' && media.host.toLowerCase() === 'vimeo') {
    return (
      <div className="aspect-[11/14] relative z-1">
        <Video
          className="h-full w-full relative z-1"
          autoplay={true}
          loop={true}
          externalId={media.originUrl}
          id={media.id}
          onPlayerInit={onPlayerInit}
        />
        <div class="absolute top-0 w-full h-full aspect-[11/14] bg-gray-100"></div>
      </div>
    );
  }

  if (mediaType === 'video' && media.sources) {
    return (
      <div className="aspect-[11/14] relative z-1">
        <Video
          className="h-full w-full relative z-1"
          autoplay={true}
          loop={true}
          isHTML5={true}
          id={media.id}
          videoSrc={media.sources[1].url}
        />
      </div>
    );
  }
};

const ProductGallery = (props) => {
  const { activeProduct, imageLayout } = useProduct();
  const { isMobile } = useWindowSize();
  const { productModels } = useProduct();
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);

  const swiperOptions = {
    slidesPerView: 1.2,
    zoom: {
      maxRatio: 5,
    },
  };

  // Custom navigation handlers
  const handlePrevSlide = useCallback(() => {
    if (mainSwiper) {
      mainSwiper.slidePrev();
    }
  }, [mainSwiper]);

  const handleNextSlide = useCallback(() => {
    if (mainSwiper) {
      mainSwiper.slideNext();
    }
  }, [mainSwiper]);

  const [lastMediaBottom, setLastMediaBottom] = useState(0);
  const mediaContainerRef = useRef(null);
  const mediaRefs = useRef([]);

  const desktopMedia = () =>
    activeProduct?.media?.edges?.map((item, idx) => (
      <div
        ref={(el) => {
          mediaRefs.current[idx] = el;
        }}
        key={item.node.id}
      >
        <ProductGalleryMedia media={item.node} />
      </div>
    ));

  useEffect(() => {
    setTimeout(() => {
      if (mediaRefs.current.length > 0) {
        const medias = mediaRefs.current.filter((el) => el);
        const lastEl = medias[medias.length - 1];
        if (lastEl) {
          const containerRect =
            mediaContainerRef.current.getBoundingClientRect();
          const rect = lastEl.getBoundingClientRect();
          setLastMediaBottom(containerRect.bottom - rect.bottom + 75);
        }
      }
    }, 2000);
  }, [activeProduct]);

  useEffect(() => {
    if (document.querySelector('.not-loaded')) {
      document
        .querySelector('[data-section="product"]')
        .classList.remove('not-loaded');
    }
  });

  return (
    <>
      {!isMobile && imageLayout === 'grid' && (
        <div
          ref={mediaContainerRef}
          className={`product-gallery-grid-layout relative grid grid-cols-2 gap-px auto-rows-min ${
            productModels.length > 0 && '-top-[1px]'
          } h-min`}
        >
          {productModels.length > 0 && (
            <ProductModels
              styles="sticky col-span-2 z-10 mx-auto"
              customStyle={{
                marginTop: `-${lastMediaBottom}px`,
                top: 'calc(100% - 75px)',
              }}
            />
          )}
          {desktopMedia()}
        </div>
      )}
      {!isMobile && imageLayout === 'slider' && (
        <div className="product-gallery-slider-layout flex gap-2">
          {/* Thumbnail rail on the left */}
          <div className="product-gallery-thumbs w-[120px] flex-shrink-0">
            <Swiper
              onSwiper={setThumbsSwiper}
              direction="vertical"
              spaceBetween={8}
              slidesPerView="auto"
              watchSlidesProgress={true}
              modules={[Thumbs]}
              className="product-gallery-thumbs-swiper h-full"
            >
              {activeProduct?.media?.edges?.map((item) => (
                <SwiperSlide key={`thumb-${item.node.id}`} className="!h-auto">
                  <ProductGalleryMedia media={item.node} isThumbnail={true} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Main slider on the right */}
          <div className="product-gallery-main flex-1 relative">
            {/* Custom Navigation Buttons */}
            <button
              onClick={handlePrevSlide}
              className="product-gallery-nav-btn product-gallery-nav-prev"
              aria-label="Previous slide"
            >
              <Icon id="chevron-left" className="w-4 h-4 text-black" />
            </button>
            <button
              onClick={handleNextSlide}
              className="product-gallery-nav-btn product-gallery-nav-next"
              aria-label="Next slide"
            >
              <Icon id="chevron-right" className="w-4 h-4 text-black" />
            </button>

            <Swiper
              spaceBetween={10}
              loop={true}
              onSwiper={setMainSwiper}
              thumbs={{
                swiper:
                  thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              modules={[Thumbs]}
              className="product-gallery-main-swiper"
            >
              {activeProduct?.media?.edges?.map((item) => (
                <SwiperSlide key={`main-${item.node.id}`}>
                  <ProductGalleryMedia media={item.node} />
                </SwiperSlide>
              ))}
            </Swiper>
            {productModels.length > 0 && (
              <ProductModels styles="relative top-0 z-10 mx-auto mt-4" />
            )}
          </div>
        </div>
      )}
      {isMobile && (
        <>
          <Swiper {...swiperOptions} allowTouchMove={true} modules={[Zoom]}>
            {activeProduct?.media?.edges?.map((item) => (
              <SwiperSlide key={item.node.id}>
                <ProductGalleryMedia media={item.node} />
              </SwiperSlide>
            ))}
          </Swiper>
          {productModels.length > 0 && (
            <ProductModels styles="relative top-0 z-10 mx-auto" />
          )}
        </>
      )}
    </>
  );
};

export default ProductGallery;
